import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MessageService } from '../../../services/message.service';

/**
 * CallbackComponent
 *
 * Handles OAuth2 callback after user authenticates with Keycloak.
 * Processes authorization code, exchanges it for tokens,
 * and redirects to intended URL or home page.
 *
 * Features:
 * - Displays loading spinner during processing
 * - Handles both web and mobile OAuth callbacks
 * - Shows error messages on failure
 * - Redirects to intended URL after success
 */
@Component({
    selector: 'myabvv-callback',
    standalone: true,
    imports: [TranslatePipe],
    templateUrl: './callback.component.html',
    styleUrls: ['./callback.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CallbackComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private messageService = inject(MessageService);

  isProcessing = true;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  async ngOnInit(): Promise<void> {
    try {
      const params = this.activatedRoute.snapshot.queryParams;

      // Check for error from OAuth provider
      if (params['error']) {
        const errorDescription = params['error_description'] || 'Authentication failed';
        this.errorMessage = `Authentication error: ${errorDescription}`;
        this.isProcessing = false;
        return;
      }

      // Check for authorization code
      if (!params['code']) {
        this.errorMessage = 'No authorization code received. Please try logging in again.';
        this.isProcessing = false;
        return;
      }

      // Process the OAuth callback
      await this.authService.handleCallback();

      this.messageService.checkUnreadMessages();

      this.successMessage = 'Login successful! Redirecting...';
      this.isProcessing = false;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      this.isProcessing = false;
    }
  }

  /**
   * Handle retry button click
   */
  onRetry(): void {
    this.router.navigate(['/auth/landing']);
  }

  /**
   * Handle home button click
   */
  onGoHome(): void {
    this.router.navigate(['/']);
  }
}
